import { Extension, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';

// Shared plumbing for newsletter content blocks (see Newsletters::RenderBlocks on the server).
// A block is saved as a placeholder, `div[data-block="<type>"][data-block-id="<uuid>"]` plus its own
// data-* attrs, and the server renders it at display time. To add one:
//
//   export const Poll = Node.create({
//     name: 'poll',
//     ...blockNodeSpec('poll', { attributes: { question: dataAttribute('question') } }),
//     addNodeView() { ... },   // editor-only UI
//   });
//
// then add `block: true` to its EXTENSION_REGISTRY entry (that is what turns on BlockIds below) and
// register the type in Newsletters::BlockRegistry on the server. Every sanitizer attribute the block
// uses must be added to config/initializers/active_support_monkey_patch.rb with it.

export function newBlockId() {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID(); // secure contexts only
  const bytes = c.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// A node attribute stored as `data-<name>` (camelCase attr name -> kebab-case data attribute).
export function dataAttribute(name, { default: defaultValue = '' } = {}) {
  const dataName = `data-${name.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`)}`;
  return {
    default: defaultValue,
    parseHTML: el => el.getAttribute(dataName) ?? defaultValue,
    renderHTML: attrs => (attrs[name] === null || attrs[name] === '' ? {} : { [dataName]: attrs[name] }),
  };
}

// Node.create() options for a block: an atom that serialises as the placeholder div. `blockId` is
// filled in by BlockIds, never typed by a person.
export function blockNodeSpec(type, { attributes = {} } = {}) {
  return {
    group: 'block',
    atom: true,
    selectable: true,

    addAttributes() {
      return {
        blockId: {
          default: null,
          parseHTML: el => el.getAttribute('data-block-id'),
          renderHTML: attrs => (attrs.blockId ? { 'data-block-id': attrs.blockId } : {}),
        },
        ...attributes,
      };
    },

    parseHTML() {
      return [{ tag: `div[data-block="${type}"]` }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes({ 'data-block': type }, HTMLAttributes)];
    },
  };
}

// Gives every block node a unique blockId: on insert, and again when pasting or duplicating copies one
// (a copy keeps the original's id, and two frames sharing an id would load each other's content).
// Enabled once per editor, by tiptap_controller, when any enabled extension is a block.
export const BlockIds = Extension.create({
  name: 'blockIds',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('blockIds'),
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some(tr => tr.docChanged)) return null;

          const seen = new Set();
          const fixes = [];
          newState.doc.descendants((node, pos) => {
            if (!('blockId' in node.type.attrs)) return;

            const id = node.attrs.blockId;
            if (!id || seen.has(id)) {
              fixes.push({ node, pos });
            } else {
              seen.add(id);
            }
          });
          if (fixes.length === 0) return null;

          const tr = newState.tr;
          fixes.forEach(({ node, pos }) => {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockId: newBlockId() });
          });
          return tr;
        },
      }),
    ];
  },
});
